import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import PageLoader from '../components/loaders/PageLoader';
import { getPlatformDetailsSelector } from '../platform/platformSelector';
import { isPlatformIframe } from '../platform/utils/platformDetailsUtils';
import { redirectLoggedInUserToWorkspace } from '../reducers/navigationActions';
import { fetchCurrentUser } from '../user/currentUserActions';
import {
    isCurrentUserAuthenticatedAndFetched,
    isCurrentUserFetched,
    isGuestSelector,
} from '../user/currentUserSelector';

type AuthRouteSessionGuardProps = {
    children?: React.ReactNode;
};

/**
 * Restores auth for auth routes before rendering forms, then redirects sessions that already existed.
 */
const AuthRouteSessionGuard: React.FC<AuthRouteSessionGuardProps> = ({ children }) => {
    const dispatch = useDispatch();
    const hasStartedAuthCheckRef = useRef(false);

    const platformDetails = useSelector(getPlatformDetailsSelector);
    const isAuthenticated = useSelector(isCurrentUserAuthenticatedAndFetched);
    const isCurrentUserLoaded = useSelector(isCurrentUserFetched);
    const isGuest = useSelector(isGuestSelector);

    // Once this route has resolved to a guest user, authActions owns later
    // successful-auth redirects so it can preserve success_redirect/from behavior.
    const hasBeenGuestUserRef = useRef(false);

    const shouldResolveAuthState = !isCurrentUserLoaded && !isGuest && !isAuthenticated;

    useEffect(() => {
        if (!shouldResolveAuthState) return;
        if (hasStartedAuthCheckRef.current) return;

        hasStartedAuthCheckRef.current = true;
        dispatch(fetchCurrentUser());
    }, [dispatch, shouldResolveAuthState]);

    useEffect(() => {
        if (!isGuest) return;

        hasBeenGuestUserRef.current = true;
    }, [isGuest]);

    useEffect(() => {
        if (!isAuthenticated) return;
        if (hasBeenGuestUserRef.current) return;

        dispatch(redirectLoggedInUserToWorkspace());
    }, [dispatch, isAuthenticated]);

    if (isPlatformIframe(platformDetails)) return null;
    if (shouldResolveAuthState || isAuthenticated) return <PageLoader />;

    return <>{children}</>;
};

/**
 * Wraps auth route components with session restoration while preserving route props.
 */
export const withAuthRouteSessionGuard =
    <Props extends object>(Component: React.ComponentType<Props>): React.FC<Props> =>
    (props) => (
        <AuthRouteSessionGuard>
            <Component {...props} />
        </AuthRouteSessionGuard>
    );

export default AuthRouteSessionGuard;
